import React, { type FC } from 'react'
import Add from '@mui/icons-material/Add'
import { ActiveAlarmFilter } from './ActiveAlarmFilter'
import { AddButtonContainer, FiltersContainer } from './Filters.style'
import type FilterOption from '../../../interfaces/FilterOption'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { ListSubheader, Tooltip } from '@mui/material'
import { ActiveLabelFilters } from './ActiveLabelFilter'
import {
  AddActiveFilterAction,
  AddActiveLabelAction,
  SetAlarmsFilterAction,
  SetMeterActivePageAction
} from '../../../redux/meter-table/meter-table-actions'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { type RootState } from '../../../redux/store'
import type { DataState } from '../../../redux/data/data-types'
import { UpdateLabelFilterChangedAction } from '../../../redux/data/data-actions'

export const Filters: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const data = useAppSelector<DataState>((state: RootState) => state.data)

  const [anchorEl, setAnchorEl] = React.useState<null | EventTarget & Element>(null)

  const handleMenuOpen = (event: React.MouseEvent): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = (): void => {
    setAnchorEl(null)
  }

  /*
      NOTE - ADDING NEW FILTER TYPES
      The code for adding new filter types is fairly reusable, but adding new Filter type requires fixing of:
        - handleAlarmsFilterAdded() -> needs to be able to handle generic filters
        - ActiveAlarmFilter.tsx -> needs to be able to handle generic filters
      This requires some rewriting of the redux code, among other things
      */
  const filters: FilterOption[] = [
    {
      name: t('alarms.activeAlarm')
    }
  ]

  const handleAlarmFilterAdded = (filterName: string): void => {
    const elementToAdd = filters.find((i: FilterOption) => i.name === filterName) // Get the generic filter object
    handleMenuClose()
    if (elementToAdd == null) {
      return
    }

    dispatch(AddActiveFilterAction({ data: elementToAdd }))
    dispatch(SetAlarmsFilterAction({ alarmsFilter: true }))
  }

  // Get distinctive labels for filtering
  const labelArrays: string[][] = data.meters.map(meter => meter.labels).filter(label => label.length > 0)
  const labelOptions: string[] = []
  labelArrays.forEach(labelArray => labelOptions.push(...labelArray))

  const distinctLabels = labelOptions.filter((label, index, labelList) => labelList.indexOf(label) === index)
  const inactiveLabelFilters: string[] = distinctLabels.filter((label: string) => !data.labelFilters.some((filterStr: string) => filterStr === label))

  const inactiveFilters: FilterOption[] = filters.filter((i: FilterOption) => !data.activeFilters.some((f: FilterOption) => f.name === i.name))

  const handleLabelFilterAdded = (filterName: string): void => {
    // Get the filter object
    const elementToAdd: string | undefined = distinctLabels.find((i) => i === filterName)
    handleMenuClose()

    if (elementToAdd == null) {
      console.warn(`Filter adding omitted -- no match for '${filterName}'`)
      return
    }

    dispatch(AddActiveLabelAction({ data: elementToAdd }))
    dispatch(UpdateLabelFilterChangedAction({ filter: elementToAdd }))
    dispatch(SetMeterActivePageAction({ activePage: 1 }))
  }

  const toolTipTitle = t('add_filter') ?? ''

  return (
        <FiltersContainer>
            <ActiveAlarmFilter/>
            <ActiveLabelFilters/>
            <Tooltip title={toolTipTitle}>
                <AddButtonContainer aria-controls='filter-menu' aria-haspopup={true} onClick={handleMenuOpen}
                                    data-testid="add-filter-button">
                    <Add style={{ color: '#ffffff', alignSelf: 'center', margin: 'auto', fontSize: '16px' }}/>
                </AddButtonContainer>
            </Tooltip>
            <Menu
                id="filter-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {inactiveFilters.map((val) => (
                    <MenuItem
                        data-testid="handle-alarm-filter-added-menuitem"
                        onClick={() => {
                          handleAlarmFilterAdded(val.name)
                        }}
                        key={val.name}
                        value={val.name}
                    >
                        {val.name}
                    </MenuItem>
                ))}
                <ListSubheader>Merkintä</ListSubheader>
                {inactiveLabelFilters.map((item) => (
                    <MenuItem
                        data-testid="handle-label-filter-added-menuitem"
                        onClick={() => {
                          handleLabelFilterAdded(item)
                        }}
                        key={item}
                        value={item}>
                        {item}
                    </MenuItem>
                ))}
            </Menu>
        </FiltersContainer>
  )
}
